/**
 * useChatSession — the chat-session "brain" extracted from
 * pages/Workspace.tsx (crucible plan 001 Step 5.3).
 *
 * Owns the pure `reduceChatState` reducer wiring (via useChatReducer),
 * the persist/hydration UI state, the two shared hydration refs
 * (hydration cursor + buffered inline-checkpoint pushes), and the four
 * chat-session effects that were previously inline in Workspace:
 *   - chat-history hydration (chat:load + agent journal, retry+backoff,
 *     initial-prompt autofire handoff, checkpoint drain)
 *   - the reattach poll (agent:get-status liveness + agent:history merge)
 *   - the "Try again" retry callback
 *   - the game/changed reset
 *
 * Behavior is byte-for-byte preserved: effect bodies + dependency arrays
 * are moved verbatim. Concerns that belong to the broader Workspace
 * (build-kickoff watchdog, pinned AskUserQuestion, create-handoff
 * dedupe ref) are threaded in as params + returned so the surviving
 * Workspace effects (agent:stream handler, watchdog) keep consuming the
 * same reducer handle + refs. The test-facing pure helpers
 * (loadChatHistoryWithAgentStatus, ChatHydrationSnapshot,
 * GameScopedCrucibleInvoke) live here and are re-exported from
 * Workspace.tsx.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
  type MutableRefObject,
} from 'react';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import {
  isFreshCreateAwaitingInitialKickoff,
  isGuidedPlanAwaitingApproval,
} from '@volley/crucible/interview/guided-plan-approval.js';
import {
  INITIAL_PROMPT_MESSAGE_ID_PREFIX,
  type AgentHistoryCursorLike,
  type InlineCheckpoint,
} from '@volley/crucible-ipc-types/ipc-channels';
import { ACTIVITY_STALE_WINDOW_MS } from './activity-freshness';
import { initialChatState, type ChatAction, type ChatEffect } from './state';
import { useChatReducer, type ReadonlyChatState } from './use-chat-reducer';
import type { ChatMessage } from './types';
import {
  projectAgentHistoryToChatMessages,
  mergeHydrationMessages,
  type AgentHistoryPageLike,
  type JournalEventLike,
} from './project-agent-history';
import { applyCreateInitialPromptFallback } from '../pages/create-handoff';
import {
  hydrationBackoffMs,
  shouldRetryHydration,
  HYDRATION_PREPARING_THRESHOLD_MS,
} from '../pages/chat-hydration-retry';
import type { InitialKickoffState } from '../workspace/run-state';
import { useCrucibleClient } from '../transport/context';

/** Reattach poll cadence (cloud-furnace 2026-06-22): how often to check
 *  whether a server-side build is running and, if so, merge fresh
 *  `agent:history` into the timeline so a reconnected / non-originating
 *  renderer catches up. `agent:get-status` (the gate) is a tiny RPC. */
export const REATTACH_POLL_INTERVAL_MS = 5000;

/**
 * #2901 — bounded cold-reconcile cadence. Incremental `agent:history` fetches
 * (§the accumulator below) hand the pod the cursor a prior page returned so it
 * serves only the delta; every RECONCILE_INTERVAL one fetch is forced COLD
 * (full snapshot, NO cursor) and REPLACES the client's accumulation wholesale.
 *
 * This is PERMANENT defence-in-depth, NOT temporary scaffolding. The runtime's
 * warm-path cursor can silently drop events with no reset signal under journal
 * rotation (seq restarts per physical file) or a build-events revision rollback
 * (#3043) — a stale cursor then yields an empty delta while the journal actually
 * advanced. The periodic cold replace heals any such drift (dropping ghost rows
 * a reset journal no longer has, AND picking up a re-based/rotated history)
 * within ONE interval, and the mandatory cold reconcile at build-terminal makes
 * a finished build's timeline exact. When #3043's t-aware cursor lands the
 * reconcile stops being load-bearing and becomes redundant insurance — LENGTHEN
 * this interval, do not delete it. 6:1 against the poll (30s) keeps the re-fetch
 * cost bounded (~1 full page per 30s vs the pre-#2901 full page per 5s).
 */
export const RECONCILE_INTERVAL_MS = REATTACH_POLL_INTERVAL_MS * 6;

/**
 * #3068 — how many CONSECUTIVE unknown (rejected) `agent:get-status` polls to
 * absorb before releasing the held liveness. A rejected read is UNKNOWN, not
 * idle: a transient pod-load blip / WSS reconnect must not be coerced to
 * `inFlight:false` (that drops the Working indicator and re-arms the kickoff
 * watchdog off a fabricated "build finished"). But holding forever is its own
 * wedge — under a genuinely gone pod every tick rejects, and pinning
 * `isAiTyping:true` would suppress the run-state staleness escalation (gated on
 * `!isAiTyping`), leaving "Working…" on screen forever with no stall.
 *
 * Sized to the lastActivityAt stale window (`ACTIVITY_STALE_WINDOW_MS`, 60s): by
 * the time we stop holding, the stamp the escalation reads (frozen when the pod
 * died) is itself stale, so releasing to idle lets a truthful 'stalled' surface
 * rather than a fabricated "finished". A single successful tick resets the count.
 *
 * `+ 1` tick of margin: the stamp is `quiet` (still suppresses the escalation)
 * AT exactly the stale window and only `stalled` strictly PAST it, so we hold
 * one extra tick to guarantee the frozen stamp has aged clear of the window by
 * release — the escalation then fires immediately, not a tick later.
 */
export const UNKNOWN_STATUS_HOLD_TICKS =
  Math.ceil(ACTIVITY_STALE_WINDOW_MS / REATTACH_POLL_INTERVAL_MS) + 1;

/**
 * #2901 r3 Bugbot — classify an `agent:history` fetch as COLD (full read) by the
 * REQUEST, not the intent. A fetch carries no cursor — so the pod serves a FULL
 * page — when: it is forced (terminal sweep), there is no accumulator yet, the
 * reconcile interval elapsed, OR the accumulator holds no cursor to send
 * (`hasCursor` false — a prior page arrived cursorless, e.g. an OLD POD or any
 * degraded read). A cursorless request always yields a full page, so it must be
 * committed cold (authoritative under `commitHistoryFetch`'s freshest+substantive
 * rule): that makes the old-pod / cursorless path self-heal on EVERY poll (cold
 * replace, back-compat with the pre-#2901 full-fetch behaviour) instead of
 * union-merging the full page and deferring the heal to the next timed reconcile.
 */
export function shouldFetchCold(args: {
  readonly forceCold: boolean;
  readonly haveAcc: boolean;
  readonly reconcileDue: boolean;
  readonly hasCursor: boolean;
}): boolean {
  return args.forceCold || !args.haveAcc || args.reconcileDue || !args.hasCursor;
}

type ChatHydrationInvokeChannel =
  | 'chat:load'
  | 'agent:get-status'
  | 'agent:history'
  | 'build-game:get-interview-state';

export type GameScopedCrucibleInvoke = (
  channel: ChatHydrationInvokeChannel,
  payload: { gameId: string },
) => Promise<unknown>;

async function readInterviewStateForChatHydration(
  invoke: GameScopedCrucibleInvoke,
  gameId: string,
  isCancelled: () => boolean,
): Promise<unknown | null> {
  // A transient bridge failure must not strand a verified fresh create. Retry
  // once in the same hydration, while continuing to fail closed if authority
  // changed or both reads fail.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await invoke('build-game:get-interview-state', { gameId });
    } catch {
      if (isCancelled()) return null;
    }
  }
  return null;
}

/**
 * #2901 — the client's per-game incremental-fetch accumulator. `agent:history`
 * warm reads return only the delta past `cursor`; the client UNIONs those deltas
 * (de-duped by envelope `id`) into `events` and RE-PROJECTS the full set each
 * time, so the reducer keeps receiving a whole-timeline projection exactly as it
 * did when every fetch pulled the full page — no partial-turn corruption. A cold
 * reconcile (or a game switch) resets the whole accumulator. Held in a ref (not
 * reducer state) — it is a fetch cache, not render state.
 */
interface AgentHistoryAccumulator {
  readonly gameId: string;
  /** The opaque watermark the last page returned; `undefined` ⇒ next fetch cold. */
  readonly cursor: AgentHistoryCursorLike | undefined;
  /** The accumulated raw journal events, de-duped by `id`, in arrival order. */
  readonly events: JournalEventLike[];
  /** `Date.now()` of the last COLD (full) fetch — drives the reconcile cadence. */
  readonly lastColdReconcileAt: number;
  /** The monotonic fetch sequence number of the fetch that set `cursor` (#2901
   *  Codex r1). Hydration + the poll are separate async effects: a LATE-resolving
   *  fetch that STARTED earlier must never overwrite the cursor a later-started
   *  fetch already installed, or the watermark regresses and the timeline rolls
   *  backwards. `commitHistoryFetch` keeps the cursor from the latest-STARTED
   *  fetch (highest seq), so ordering — not resolve-race — decides the cursor. */
  readonly cursorSeq: number;
  /** The `mySeq` of the last fetch that AUTHORITATIVELY replaced `events` — a
   *  substantive freshest cold read (or the initial cold seed). A fetch that
   *  physically STARTED before this generation (`mySeq < lastAuthoritativeColdSeq`)
   *  read a strictly-older journal, so its snapshot is wholly superseded: any id
   *  it carries that this authoritative cold LACKS is either a subset (append-only
   *  ⇒ union is a no-op) or a ghost the reset/rotation dropped (⇒ union would
   *  RESTORE it). Such a stale fetch's events are therefore NOT unioned (Bugbot:
   *  "stale fetches restore reconciled ghosts"). `0` ⇒ no authoritative cold yet
   *  (real seqs start at 1, so nothing is ever superseded against the sentinel). */
  readonly lastAuthoritativeColdSeq: number;
}

/**
 * #2901 Codex r1 — the single generation-guarded commit for EVERY `agent:history`
 * fetch (hydration seed, warm delta, cold reconcile). It resolves the
 * hydration/poll (and reconcile/delta) async race without comparing opaque
 * cursors:
 *
 *   - `mySeq` is stamped when the fetch STARTS (monotonic per game). A fetch that
 *     started later saw a later journal, so its cursor is newer. On commit the
 *     accumulator keeps the cursor from the highest-seq fetch — a late-resolving
 *     older fetch NEVER regresses the watermark.
 *   - Events always UNION by id (additive; a stale fetch can only contribute
 *     events a newer one lacked — strictly better than dropping it).
 *   - Only the FRESHEST cold fetch REPLACES events (drops ghost rows a reset/
 *     rotated journal no longer has); a stale cold fetch or any warm fetch merges.
 *     A cold reconcile adopts its fresh watermark even when lower than a stale
 *     pre-rotation one — that lower-but-correct cursor is exactly the #3043 heal.
 *
 * Pure + exported so the ordering matrix is unit-pinned without React timing.
 *
 * #3528 (Codex r3, MAJOR) — returns `{ accumulator, authoritativeReplace }`.
 * `authoritativeReplace` is an EXPLICIT per-commit outcome: `true` ONLY on the
 * two branches that REPLACE `events` (a substantive cold seed, and the freshest-
 * substantive `authoritativeCold` reconcile), `false` on every UNION branch. The
 * caller dispatches `chat/agent-history-merged`'s `authoritative` flag off THIS
 * boolean — never off the request-level `cold` flag, and never re-derived from a
 * seq counter. The prior `lastAuthoritativeColdSeq === mySeq` derivation was
 * unsafe: `historyFetchSeqRef` used to be RESET to 0 on a same-game hydration
 * retry while the reattach-poll effect stayed live, so a post-reset warm-union
 * tick could reuse a seq equal to a prior cold fetch's stamped
 * `lastAuthoritativeColdSeq` and report authoritative on a NON-authoritative
 * (union) branch — deleting rows. The bulletproof round also made the seq
 * monotonic (the reset is gone), but the explicit boolean stays as the primary,
 * seq-independent guard.
 */
export interface CommitHistoryFetchResult {
  readonly accumulator: AgentHistoryAccumulator;
  /** Did THIS commit perform an authoritative REPLACE of `events` (⇒ the merge
   *  is authoritative about row absence)? True only on a substantive cold seed
   *  or the freshest-substantive cold reconcile; false on every union merge. */
  readonly authoritativeReplace: boolean;
}

export function commitHistoryFetch(
  current: AgentHistoryAccumulator | null,
  gameId: string,
  args: {
    readonly mySeq: number;
    readonly cold: boolean;
    readonly items: readonly JournalEventLike[];
    readonly nextCursor: AgentHistoryCursorLike | undefined;
    readonly now: number;
    /**
     * #3528 (LEG 2) — the corrupt-line count the read backing `items` reported
     * (`agent.history@1`'s `parseErrors`). When `> 0` the cold read is DEGRADED:
     * a previously-projected line may have gone unreadable, so the page is NOT a
     * complete snapshot of row absence. A degraded cold read therefore NEVER
     * takes a replace branch — it falls to UNION/retain and reports
     * `authoritativeReplace: false`, so the reducer keeps (never deletes) the
     * held rows (crucible#3043 "never act on uncertainty in the destructive
     * direction"). Optional (defaults 0) so the pure ordering tests, which do
     * not exercise degradation, are unaffected.
     */
    readonly parseErrors?: number;
  },
): CommitHistoryFetchResult {
  // #3528 (LEG 2) — a degraded read (corrupt lines skipped) can never be
  // authoritative about ABSENCE; suppress both replace branches below.
  const degradedRead = (args.parseErrors ?? 0) > 0;
  const cur = current !== null && current.gameId === gameId ? current : null;
  if (cur === null) {
    // First fetch for this game (`current` is null, or belongs to a DIFFERENT
    // game — the guard above treats a cross-game accumulator as null, which is
    // how game isolation holds now that the accumulator is no longer explicitly
    // reset): it owns the whole accumulator — replace events, adopt cursor,
    // stamp the reconcile clock.
    // A cold seed is the first authoritative baseline; a (defensive) warm seed has
    // none yet, so it leaves the sentinel `0`. The seed is an AUTHORITATIVE
    // REPLACE (about absence) only when it is a SUBSTANTIVE cold read — an empty
    // cold seed stamps the generation but must not license deletion against an
    // empty page.
    const seedSubstantive = args.items.length > 0;
    return {
      accumulator: {
        gameId,
        cursor: args.nextCursor,
        cursorSeq: args.mySeq,
        events: dedupJournalById([...args.items]),
        lastColdReconcileAt: args.now,
        // #3528 (LEG 2) — a DEGRADED cold seed is NOT an authoritative baseline:
        // leave the sentinel `0` so an older-but-complete fetch resolving later
        // can still UNION back a line this degraded read could not parse (the
        // supersession guard must not lock in a lossy read as the baseline).
        lastAuthoritativeColdSeq: args.cold && !degradedRead ? args.mySeq : 0,
      },
      // A degraded cold seed stamps the accumulator (it is all we hold on the
      // first fetch) but is NOT authoritative about absence.
      authoritativeReplace: args.cold && seedSubstantive && !degradedRead,
    };
  }
  // Freshest ⇔ no later-started fetch has committed since this one started.
  const freshest = args.mySeq >= cur.cursorSeq;
  const substantive = args.items.length > 0;
  // A COLD read is AUTHORITATIVE — it REPLACES events (dropping ghost rows a
  // reset/rotated journal no longer has) and ADOPTS its watermark (even a lower
  // post-rotation one: the #3043 heal) — ONLY when it is the freshest AND
  // actually returned a snapshot. A freshest-but-EMPTY cold read (a failed /
  // degraded / transient read that returned nothing while we already hold
  // content) must NOT blank the accumulator or regress the cursor. This is what
  // stops a failed hydration journal read from dropping the completed turn the
  // poll already merged (#2901 Codex r2 HIGH-1).
  //
  // #3528 (LEG 2) — a DEGRADED cold read (corrupt lines skipped) is likewise NOT
  // authoritative: a previously-projected line may have gone unreadable, so
  // replacing `events` with this partial snapshot would DROP the held row and no
  // union could restore it. Fall to the UNION/retain branch instead (keep held
  // rows, add any newly-readable ones), reporting `authoritativeReplace: false`.
  const authoritativeCold = args.cold && freshest && substantive && !degradedRead;
  if (authoritativeCold) {
    return {
      accumulator: {
        gameId,
        cursor: args.nextCursor,
        cursorSeq: args.mySeq,
        events: dedupJournalById([...args.items]),
        lastColdReconcileAt: args.now,
        // This full read is the new authoritative baseline: fetches that started
        // before it are now superseded (see the union guard below).
        lastAuthoritativeColdSeq: args.mySeq,
      },
      // The one branch (besides a substantive cold seed) that REPLACES events:
      // it is authoritative about absence.
      authoritativeReplace: true,
    };
  }
  // Non-authoritative: UNION events (additive, id-deduped). Adopt the cursor only
  // when this fetch is the freshest AND is either a warm delta (its returned
  // watermark is monotonic past the one it was handed) or a substantive cold
  // read; an empty/failed cold read keeps the current watermark.
  const adopt = freshest && (!args.cold || substantive);
  // r3 Bugbot Medium — a freshest cold read that healed NOTHING must not credit
  // the reconcile clock: postponing the next reconcile a full interval on a
  // degraded/empty read that returned nothing lets stale-cursor drift persist.
  // BUT when there is nothing held to heal (empty accumulator), the empty read
  // both healed and missed nothing, so we DO advance — otherwise a genuinely
  // empty game would force a cold reconcile every single tick.
  const creditReconcileClock = args.cold && freshest && cur.events.length === 0;
  // Bugbot ("stale fetches restore reconciled ghosts"): a fetch that physically
  // STARTED before the last authoritative cold replace read a strictly-older
  // journal, so its snapshot is wholly superseded — any id it carries that the
  // cold LACKS is either a subset (union is a no-op) or a ghost the reset/rotation
  // dropped (union would RESTORE it). Do not union its events. A superseded fetch
  // is by construction NOT freshest (its `mySeq < lastAuthoritativeColdSeq <=
  // cursorSeq`), so this never interacts with cursor adoption; it only blocks the
  // ghost-restoring re-union. Warm deltas that started AFTER the cold (higher seq)
  // still union — their events are legitimately past the cold's snapshot.
  const supersededByCold = args.mySeq < cur.lastAuthoritativeColdSeq;
  return {
    accumulator: {
      gameId,
      cursor: adopt ? args.nextCursor : cur.cursor,
      cursorSeq: adopt ? args.mySeq : cur.cursorSeq,
      events: supersededByCold ? cur.events : dedupJournalById([...cur.events, ...args.items]),
      lastColdReconcileAt: creditReconcileClock ? args.now : cur.lastColdReconcileAt,
      lastAuthoritativeColdSeq: cur.lastAuthoritativeColdSeq,
    },
    // UNION branch — a warm delta, a stale/degraded/empty/superseded cold read:
    // it RETAINS the prior accumulator (never REPLACES), so it is NOT
    // authoritative about absence. This is the branch a degraded cold read falls
    // into (#3528), which is exactly why `authoritative` must not be the request
    // `cold` flag: `cold === true` here, but the merge must NOT delete rows.
    authoritativeReplace: false,
  };
}

/** Extract the raw journal items from an `agent.history@1` page (structural —
 *  the wire result is `unknown`). `items` is canonical; `events` is the legacy
 *  fallback the projection also accepts. */
function extractJournalItems(page: unknown): JournalEventLike[] {
  if (page && typeof page === 'object') {
    const items = (page as { items?: unknown }).items;
    if (Array.isArray(items)) return items as JournalEventLike[];
    const events = (page as { events?: unknown }).events;
    if (Array.isArray(events)) return events as JournalEventLike[];
  }
  return [];
}

/** #3528 (LEG 2) — extract the `agent.history@1` page's `parseErrors` count
 *  (structural — the wire result is `unknown`). A finite positive number means
 *  the read was DEGRADED (corrupt lines skipped); anything else — absent (an old
 *  pod that predates the field), non-number, negative, NaN — reads as `0`, the
 *  same optimistic posture as before the field existed. */
function extractPageParseErrors(page: unknown): number {
  if (page && typeof page === 'object') {
    const raw = (page as { parseErrors?: unknown }).parseErrors;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  }
  return 0;
}

// #2901 r5/r6 — cursor extraction + shape validation lives in a pure, React-free
// module (`./history-cursor`) so the crucible-gui contract-pin can import it into
// a runtime-boot test to assert guard⊆parser equivalence. Imported for the hook's
// own use + re-exported for its tests. The validation fully mirrors the parser.
import { extractHistoryCursor, isValidHistoryCursor } from './history-cursor';
export { extractHistoryCursor, isValidHistoryCursor };

/** Union journal events, keeping the FIRST occurrence of each envelope `id`
 *  (the append-only journal never mutates a line, so first==authoritative). An
 *  event with no string `id` is unprojectable anyway (the projection drops it),
 *  but is retained here rather than silently dropped at the fetch layer. */
function dedupJournalById(events: readonly JournalEventLike[]): JournalEventLike[] {
  const seen = new Set<string>();
  const out: JournalEventLike[] = [];
  for (const ev of events) {
    const id = typeof ev.id === 'string' ? ev.id : undefined;
    if (id !== undefined) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(ev);
  }
  return out;
}

export interface ChatHydrationSnapshot {
  messages: ChatMessage[];
  agentInFlight: boolean;
  /** Durable create authority, not inferred from chat-history shape. */
  initialPromptAutofireAllowed: boolean;
  /** #2901 — the raw journal items + cursor from the cold hydration fetch, so
   *  the reattach poll's incremental accumulator can be SEEDED from the one full
   *  page hydration already pulled (rather than the first poll tick paying a
   *  second full fetch to seed). Empty/undefined for a desktop / pre-journal
   *  game (the journal fetch caught to `null`). */
  journalItems: JournalEventLike[];
  journalCursor: AgentHistoryCursorLike | undefined;
  /** #3528 (LEG 2) — the cold hydration page's `parseErrors` count, threaded so
   *  the accumulator SEED commit falls closed on a degraded read: without it, a
   *  degraded hydration RETRY (freshest + substantive) would authoritative-
   *  replace the accumulator and DROP a turn a prior poll merged, which
   *  `chat/loaded` then loses from the timeline. `0` for a desktop / pre-journal
   *  game (empty page). */
  journalParseErrors: number;
  /** #2901 Codex r2 — the fetch-sequence stamped at the PHYSICAL `agent:history`
   *  fetch (via the caller's `stampFetchSeq`), so start-order reflects when the
   *  journal was actually read, not the logical hydration start before the slow
   *  `chat:load`. Undefined when no stamp callback was provided. */
  journalFetchSeq: number | undefined;
}

/**
 * Read authoritative agent liveness for a game from `agent:get-status`.
 * Returns `true`/`false` on success, or `undefined` when the read FAILS
 * (crucible#2956 Codex r7). A failed liveness read is NOT authoritative idle:
 * treating it as `false` would wrongly terminalise a genuinely live turn as
 * errored. The journal projection preserves `in-flight` on `undefined`, so an
 * unknown liveness never forces a stall render — the next successful poll
 * resolves it. Callers MUST read this STRICTLY AFTER the `agent:history`
 * snapshot the liveness will judge, so a turn that starts between the two reads
 * is seen as in-flight rather than stamped stale-idle.
 */
export async function readAgentLiveness(
  getStatus: () => Promise<unknown>,
): Promise<boolean | undefined> {
  try {
    const status = (await getStatus()) as { inFlight?: unknown } | null | undefined;
    return status?.inFlight === true;
  } catch {
    return undefined;
  }
}

/**
 * Loads persisted chat history, then snapshots whether an agent turn is
 * in flight. Agent status must be queried after history returns — a
 * parallel `agent:get-status` + slow `chat:load` can predate hydration
 * and yield a stale `inFlight` (busy UI with no stream, or initial-
 * prompt auto-send racing an active turn).
 */
export async function loadChatHistoryWithAgentStatus(
  invoke: GameScopedCrucibleInvoke,
  gameId: string,
  isCancelled: () => boolean,
  options?: {
    /** #2901 Codex r2 — stamp the incremental-fetch start-order at the PHYSICAL
     *  `agent:history` fetch (called just before it). Threaded from the hook so
     *  a hydration journal fetch that physically runs AFTER a poll fetch is
     *  correctly the freshest (the seq is not stamped before the slow chat:load). */
    readonly stampFetchSeq?: () => number;
  },
): Promise<ChatHydrationSnapshot | null> {
  const saved = await invoke('chat:load', { gameId });
  if (isCancelled()) return null;
  // 3c — read the durable agent journal and project it into chat messages, so a
  // cloud Furnace build survives a full browser reload (chat:load is desktop-only
  // in cookie mode; the journal is the cloud hydration source). Best-effort: a
  // desktop build / pre-journal game / unrouted stub yields `[]` (caught), and
  // the merge collapses to just `saved` — byte-identical to the prior behaviour.
  // #2901 Codex r2 — stamp start-order HERE (physical fetch), not before chat:load.
  const journalFetchSeq = options?.stampFetchSeq?.();
  const journalPage = await invoke('agent:history', { gameId }).catch(() => null);
  if (isCancelled()) return null;
  const savedMessages = (saved as ChatMessage[] | null | undefined) ?? [];
  // Read authoritative liveness AFTER the journal snapshot (crucible#2956 Codex
  // r6/r7): an idle agent's unterminated turn must hydrate terminal, not a
  // forever-"running" bubble — but the sample must be FRESH relative to the
  // history it judges (status strictly after `agent:history`, itself after
  // `chat:load`), and a FAILED read is "unknown" (undefined → the projection
  // preserves in-flight), never authoritative idle that would wrongly stall a
  // live turn.
  const interviewState = await readInterviewStateForChatHydration(invoke, gameId, isCancelled);
  if (isCancelled()) return null;
  // Keep liveness after durable state for the renderer projection. This pair is
  // deliberately only a UX prefilter for initial autofire: the main process
  // takes an atomic per-game claim, then rechecks current durable state and
  // liveness before it dispatches. Alternating renderer samples cannot close
  // the gap because another renderer can always start after the final await.
  const agentInFlight = await readAgentLiveness(() => invoke('agent:get-status', { gameId }));
  if (isCancelled()) return null;
  const awaitingPlanApproval = isGuidedPlanAwaitingApproval(interviewState);
  // A durably written Guided handoff is the logical end of its interview
  // operation. The SDK may still be physically unwinding, but stale adapter
  // liveness must not rehydrate a typing turn over the plan-review boundary.
  const rendererAgentInFlight = awaitingPlanApproval ? false : agentInFlight;
  const projected = projectAgentHistoryToChatMessages(journalPage as AgentHistoryPageLike | null, {
    agentInFlight: rendererAgentInFlight,
  });
  const messages =
    projected.length > 0 ? mergeHydrationMessages(savedMessages, projected) : savedMessages;
  return {
    messages,
    // `undefined` (read failed) is NOT authoritatively in flight — the reducer
    // must not rehydrate it as a live typing turn.
    agentInFlight: rendererAgentInFlight === true,
    initialPromptAutofireAllowed:
      isFreshCreateAwaitingInitialKickoff(interviewState) && agentInFlight === false,
    // #2901 — expose the raw page so the poll's accumulator seeds from this cold
    // fetch. Derived from the SAME `journalPage` the projection above used.
    journalItems: extractJournalItems(journalPage),
    journalCursor: extractHistoryCursor(journalPage),
    // #3528 (LEG 2) — the SAME page's degraded-read count, so the seed commit
    // stays non-authoritative (retain, never replace) on a degraded read.
    journalParseErrors: extractPageParseErrors(journalPage),
    journalFetchSeq,
  };
}

export interface UseChatSessionParams {
  gameId: string | undefined;
  createInitialPrompt: string | null;
  /** Build-kickoff watchdog state setter (Workspace-owned). */
  setInitialKickoff: Dispatch<SetStateAction<InitialKickoffState>>;
  /** Create-handoff dedupe ref (Workspace-owned; shared with route effects). */
  appliedCreateHandoffPromptKeyRef: MutableRefObject<string | null>;
}

export interface UseChatSessionResult {
  chat: ReturnType<typeof useChatReducer>['state'];
  chatDispatch: (action: ChatAction) => void;
  setInterpreter: (fn: (effect: ChatEffect) => void) => void;
  getChatState: () => ReadonlyChatState;
  /** Hydration cursor: which gameId the checkpoint buffer has been flushed for. */
  hydratedForGameIdRef: MutableRefObject<string | null>;
  /** Inline-checkpoint pushes buffered during the pre-hydration window. */
  pendingCheckpointPushesRef: MutableRefObject<
    Array<{ gameId: string; checkpoint: InlineCheckpoint }>
  >;
  hydrationLongRunning: boolean;
  buildInFlightFromPoll: boolean;
  setBuildInFlightFromPoll: Dispatch<SetStateAction<boolean>>;
  chatPersistError: SerialisedError | null;
  setChatPersistError: Dispatch<SetStateAction<SerialisedError | null>>;
  handleRetryHydration: () => void;
}

export function useChatSession(params: UseChatSessionParams): UseChatSessionResult {
  const client = useCrucibleClient();
  const { gameId, createInitialPrompt, setInitialKickoff, appliedCreateHandoffPromptKeyRef } =
    params;

  // Chat state — owned by the pure `reduceChatState` reducer in
  // `chat/state.ts`. The reducer + its tests pin every desync we shipped
  // through alpha.41 -> alpha.44 (queue while in-flight, terminal vs
  // non-terminal errors, AskUserQuestion gate dispatch, cross-game reset).
  const {
    state: chat,
    dispatch: chatDispatch,
    setInterpreter,
    getState: getChatState,
  } = useChatReducer(initialChatState(gameId ?? ''));

  const [chatPersistError, setChatPersistError] = useState<SerialisedError | null>(null);
  const [hydrationAttemptKey, setHydrationAttemptKey] = useState(0);
  const [hydrationLongRunning, setHydrationLongRunning] = useState(false);

  // `activity:checkpoint` subscriber. Codex round-1 HIGH #1: between
  // Workspace mount and the `chat:load` IPC resolving, an
  // `activity:checkpoint` push could land + dispatch
  // `chat/checkpoint-received` (inserts the row into `state.messages`)
  // → then the in-flight `chat:load` resolves and `chat/loaded` REPLACES
  // `state.messages` with the snapshot the disk had BEFORE the emitter's
  // queued append committed. The freshly-inserted checkpoint vanishes
  // until the next mount.
  //
  // Fix: buffer in-flight checkpoint pushes that arrive before
  // hydration completes, then replay them after `chat/loaded` has been
  // dispatched. The replay is per-`gameId`-scoped via the `gameId`
  // closure on the subscription effect; a cross-game cleanup wipes
  // the buffer (the new game's state has its own).
  //
  // Implemented with a single boolean `hydratedForGameIdRef` and a
  // local `pendingPushesRef` keyed by the gameId we're currently
  // hydrating against. The buffer is drained once the load resolves;
  // any subsequent pushes flow through directly.
  const hydratedForGameIdRef = useRef<string | null>(null);
  const pendingCheckpointPushesRef = useRef<
    Array<{ gameId: string; checkpoint: InlineCheckpoint }>
  >([]);

  // #2901 — the incremental `agent:history` accumulator, shared between the
  // hydration effect (which seeds it from the cold hydration page) and the
  // reattach poll (which pages warm deltas onto it and periodically cold-
  // reconciles). Held in a ref: it is a fetch cache, not render state. Reset to
  // null on game switch so a stale cursor / ghost events never cross games.
  const historyAccumulatorRef = useRef<AgentHistoryAccumulator | null>(null);
  // #2901 Codex r1 — monotonic per-game fetch counter. Every agent:history
  // fetch (hydration seed / warm delta / cold reconcile) stamps its start order
  // here; `commitHistoryFetch` uses it so a late-resolving older fetch can't
  // regress the cursor a later-started fetch installed. Reset on game switch.
  const historyFetchSeqRef = useRef<number>(0);

  // Load chat history from game repo. The auto-fire of an
  // `initial-prompt-` shape is now handled inside the reducer's
  // `chat/loaded` action — see state.ts. The IPC kickoff that used
  // to live here is one of the effects that action returns, so the
  // interpreter wired up below picks it up.
  //
  // Cross-game race guard: chat:load is async, and gameId can change
  // (React Router /workspace/:gameId reuses this component). Without
  // a cancellation flag, a slow chat:load Promise from gameId=A
  // could resolve AFTER we've navigated to B and reset state — and
  // dispatch A's history onto B's reducer state. Worse, if A had a
  // pending initial-prompt the autofire would emit `ipc/agent-send`
  // tagged with `state.gameId` (now B) carrying A's prompt text:
  // cross-game send. The cleanup flag rejects late resolutions.
  //
  // Agent status is queried only after history returns — see
  // `loadChatHistoryWithAgentStatus` — so `agentInFlight` reflects
  // the moment hydration applies, not the start of a slow load.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    // Crucible #602 PR #C4 (Issue E) — reset hydration state for this
    // gameId. A cross-game navigation reuses the Workspace instance, so
    // the hydration cursor needs to flip back to "not hydrated for
    // gameId X" until X's chat:load resolves. Buffered pushes from a
    // previous game's pre-hydration window are wiped — they're tagged
    // with the OLD gameId and the activity:checkpoint subscription's
    // cross-game guard would have dropped them anyway.
    hydratedForGameIdRef.current = null;
    pendingCheckpointPushesRef.current = [];
    // #3528 (bulletproof round) — DO NOT reset the incremental accumulator here.
    // This effect re-runs on a same-game hydration RETRY (hydrationAttemptKey
    // bump), and nulling the accumulator on a retry turned the re-hydration's
    // commit into a destructive SEED (cur===null) that blindly replaced events —
    // dropping a turn a prior poll had merged, which `chat/loaded` then lost from
    // the timeline (worst on a DEGRADED retry read, LEG 2). Leaving it intact
    // routes the retry through the UNION/authoritative path (cur!==null,
    // consistent with the poll), where the LEG 2 parseErrors gate retains held
    // rows on a degraded read. Cross-game isolation does NOT depend on this
    // reset: the accumulator is game-scoped — `commitHistoryFetch` treats a
    // different-game accumulator as null (fresh seed) and the reattach poll's
    // `haveAcc` requires `accAtStart.gameId === gameId` — so a lingering prior
    // game's accumulator is never read for the new game (it is overwritten by the
    // new game's first cold seed). Mirrors the monotonic-seq treatment below.
    // #3528 (bulletproof round) — DO NOT reset the fetch sequence here. It is a
    // monotonic generation counter shared with the reattach-poll effect, whose
    // deps EXCLUDE `hydrationAttemptKey`, so that effect stays live across a
    // same-game hydration retry. Zeroing the seq while the poll keeps
    // incrementing let a post-reset fetch reuse a seq a live/earlier fetch
    // already holds — regressing the cursor, restoring a reconciled ghost, or
    // (pre-round-3) reporting a union tick as authoritative. Absolute seq values
    // are never compared across games (each game's accumulator is seeded fresh,
    // `cur === null`); only ORDER matters, and a never-reset counter keeps order
    // total and correct across both game switches and hydration retries.

    // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — "preparing your
    // workspace" timer. If this single load is still pending past the
    // threshold, flip the surface from the quick loading affordance to the
    // explicit long-provisioning message. Cleared on settle / unmount.
    setHydrationLongRunning(false);
    const preparingTimer = setTimeout(() => {
      if (!cancelled) setHydrationLongRunning(true);
    }, HYDRATION_PREPARING_THRESHOLD_MS);

    // GAP-3 — drain checkpoint pushes buffered during the pre-hydration
    // window + flip the hydration cursor. Shared by BOTH the success and the
    // terminal-failure path (preserving the Crucible #602 PR #C4 invariant
    // that even a failed load still honours buffered checkpoints rather than
    // dropping them; the reducer's cross-game + duplicate-id guards keep
    // this safe).
    const flipCursorAndDrainCheckpoints = () => {
      hydratedForGameIdRef.current = gameId;
      const pending = pendingCheckpointPushesRef.current;
      pendingCheckpointPushesRef.current = [];
      for (const push of pending) {
        if (push.gameId !== gameId) continue; // belt-and-braces
        chatDispatch({
          type: 'chat/checkpoint-received',
          gameId: push.gameId,
          checkpoint: push.checkpoint,
        });
      }
    };

    const applyHydration = (hydration: ChatHydrationSnapshot, seq: number) => {
      const { messages: loadedMessages, agentInFlight } = hydration;
      // #2901 — seed the incremental accumulator from the cold hydration page so
      // the reattach poll pages warm deltas off THIS cursor instead of paying a
      // second full fetch to seed. Routed through `commitHistoryFetch` with the
      // journal fetch's physical start-order `seq` (Codex r1/r2): if a poll tick
      // has ALREADY committed since this journal fetch physically started (slow
      // hydration + fast first poll), this LATE seed merges its items but keeps
      // the poll's newer cursor; if this fetch is the freshest it replaces +
      // adopts. No rollback, no watermark regression.
      // This commit only primes the incremental accumulator; it dispatches
      // `chat/loaded` (not `chat/agent-history-merged`), so its
      // `authoritativeReplace` outcome is intentionally unused here.
      // #3528 (LEG 2, bulletproof round) — pass the page's `parseErrors` so a
      // DEGRADED read is never treated as authoritative. Two cases:
      //   - FIRST hydration (cur===null → seed): there is nothing held to
      //     retain, so the seed stores its items but stays non-authoritative and
      //     sets NO baseline (`lastAuthoritativeColdSeq` 0) on a degraded read.
      //   - same-game RETRY (cur!==null now that the accumulator is NOT reset on
      //     a hydrationAttemptKey re-run — see the effect top): the commit goes
      //     through the UNION/authoritative path, so a degraded retry UNIONS/
      //     RETAINS the turns a prior poll merged (never replaces), and
      //     `chat/loaded` keeps them. This is the case the old accumulator reset
      //     broke by forcing every retry back onto the destructive seed path.
      historyAccumulatorRef.current = commitHistoryFetch(historyAccumulatorRef.current, gameId, {
        mySeq: seq,
        cold: true,
        items: hydration.journalItems,
        nextCursor: hydration.journalCursor,
        now: Date.now(),
        parseErrors: hydration.journalParseErrors,
      }).accumulator;
      const handoffKey = createInitialPrompt && gameId ? `${gameId}\n${createInitialPrompt}` : null;
      const createFallback =
        handoffKey && appliedCreateHandoffPromptKeyRef.current !== handoffKey
          ? applyCreateInitialPromptFallback({
              loadedMessages,
              agentInFlight,
              routePrompt: createInitialPrompt,
            })
          : { messages: loadedMessages, applied: false };
      if (handoffKey && createFallback.applied) {
        appliedCreateHandoffPromptKeyRef.current = handoffKey;
      }
      // #2901 Codex r2 (HIGH-1: duplicate send) — derive the ONE `chat/loaded`
      // message set from the UNION of this hydration page AND the guarded
      // accumulator (which may already hold NEWER poll-merged turns). A single
      // dispatch off the union means no render rollback behind the poll's newer
      // merge. The separate orchestration gate below combines this message
      // shape with durable create state before authorizing an autofire.
      const accEvents = historyAccumulatorRef.current?.events ?? [];
      // r3 Bugbot Medium — DO NOT apply the hydration-time `agentInFlight` when
      // re-projecting the accumulator: it is stale relative to the poll (which
      // may have merged a turn whose liveness moved since this hydration sampled
      // it). Pass `undefined` (preserve) so a turn renders done ONLY on its own
      // terminal evidence; terminalising an unterminated turn is left to the
      // poll's fresh-liveness merges — never wrongly cleared by a stale flag.
      const accProjected =
        accEvents.length > 0
          ? projectAgentHistoryToChatMessages(accEvents, { agentInFlight: undefined })
          : [];
      const messagesForHydration =
        accProjected.length > 0
          ? mergeHydrationMessages(createFallback.messages, accProjected)
          : createFallback.messages;
      const shouldAutofireInitialPrompt =
        hydration.initialPromptAutofireAllowed &&
        !agentInFlight &&
        messagesForHydration.length === 1 &&
        messagesForHydration[0]?.role === 'user' &&
        messagesForHydration[0].id.startsWith(INITIAL_PROMPT_MESSAGE_ID_PREFIX);
      setInitialKickoff(
        shouldAutofireInitialPrompt
          ? { status: 'waiting-for-renderer-ready', startedAt: Date.now() }
          : { status: 'idle', startedAt: null },
      );
      // `chat/loaded` moves hydrationStatus -> 'ready' (see state.ts).
      chatDispatch({
        type: 'chat/loaded',
        messages: messagesForHydration,
        agentInFlight,
      });
      if (shouldAutofireInitialPrompt) {
        chatDispatch({ type: 'chat/autofire-initial-prompt', now: Date.now() });
      }
      // Successful load (even if empty) clears any previous persist error.
      setChatPersistError(null);
      // Crucible #602 PR #C4 (Issue E) — flip hydration cursor + drain
      // any checkpoint pushes that landed during the in-flight window.
      // Drain order is FIFO (push-arrival order); the reducer's
      // duplicate-id guard handles any push whose checkpoint id is
      // ALSO present in the just-rehydrated `saved` array (the emitter
      // wrote it to disk and pushed; we saw both copies).
      flipCursorAndDrainCheckpoints();
    };

    // GAP-3 — bounded retry-with-backoff loop. The success path is
    // unchanged; the failure path now distinguishes RETRYABLE transport
    // errors (retry up to MAX_HYDRATION_ATTEMPTS with exponential backoff)
    // from terminal/non-retryable errors (corrupt-history category 'state',
    // or budget exhausted), which fall through to a legible error state.
    // The whole loop respects the cross-game `cancelled` guard: every await
    // point re-checks it so a nav-away/unmount aborts the retries.
    const runHydrationWithRetry = async () => {
      let attemptsSoFar = 0;
      for (;;) {
        let hydration: ChatHydrationSnapshot | null;
        try {
          hydration = await loadChatHistoryWithAgentStatus(
            (channel, payload) => client.invoke(channel, payload),
            gameId,
            () => cancelled,
            // #2901 Codex r2 — stamp start-order at the PHYSICAL agent:history
            // fetch (inside the loader, after chat:load), not here before it.
            { stampFetchSeq: () => (historyFetchSeqRef.current += 1) },
          );
        } catch (err: unknown) {
          if (cancelled) return;
          attemptsSoFar += 1;
          const decoded = unflattenIpcError(err);
          if (shouldRetryHydration(decoded, attemptsSoFar)) {
            // Wait the backoff, re-checking the cancel guard on the far
            // side so a nav-away during the wait aborts cleanly.
            await new Promise((resolve) =>
              setTimeout(resolve, hydrationBackoffMs(attemptsSoFar - 1)),
            );
            if (cancelled) return;
            continue;
          }
          // Terminal failure (non-retryable OR budget exhausted). Drive
          // hydrationStatus -> error so the surface renders a legible error
          // state instead of a silent permanent blank.
          //
          // ONE surface per failure class (Cursor Bugbot, PR #2026): the
          // in-panel retry block (`showHydrationRetryError`) renders for a
          // RETRYABLE transport error (category !== 'state'); the
          // `chatPersistError` banner renders for everything else. Setting
          // both for a retryable error double-surfaced the same failure
          // (banner + centered "Couldn't load chat history"). So set the
          // persist banner ONLY when the in-panel block will NOT handle it —
          // i.e. the corrupt-history path (CrucibleError category 'state',
          // which keeps its existing one-shot recovery banner pointing at the
          // backup file) and any other non-retryable error. The predicate
          // here is the exact complement of `showHydrationRetryError`.
          const handledByInPanelRetry = decoded.retryable === true && decoded.category !== 'state';
          if (!handledByInPanelRetry) {
            setChatPersistError(decoded);
          }
          chatDispatch({ type: 'chat/hydration-failed', error: decoded });
          // Crucible #602 PR #C4 (Issue E) — even on the failure path we
          // still flip the hydration cursor + drain buffered checkpoints,
          // so a user with a recoverable corrupt history doesn't lose a
          // checkpoint that fired during the load attempt.
          flipCursorAndDrainCheckpoints();
          return;
        }
        if (!hydration) return; // cancelled mid-load
        // #2901 Codex r2 — use the seq stamped at the PHYSICAL journal fetch.
        applyHydration(hydration, hydration.journalFetchSeq ?? (historyFetchSeqRef.current += 1));
        return;
      }
    };

    void runHydrationWithRetry();

    return () => {
      cancelled = true;
      clearTimeout(preparingTimer);
    };
    // hydrationAttemptKey is in the deps so the "Try again" button (which
    // bumps it) re-runs the load for the same game.
  }, [gameId, chatDispatch, createInitialPrompt, hydrationAttemptKey]);

  // Latest `agent:get-status` inFlight from the reattach poll below. Exposed as
  // state (not just a poll-local) so the build-kickoff watchdog can stand down
  // when the pod confirms a build IS running: the watchdog's other signal
  // (`activity:state` → activitySnapshot) is fed only by chunks this GUI itself
  // streams, so it's structurally DARK for a detached build the GUI merely
  // observes — which made the watchdog fire a spurious "kickoff retry" at every
  // plan approval. `agent:get-status` reads the pod refcount and is reliable for
  // the detached case. Defaults false; the poll flips it within one tick (5s).
  const [buildInFlightFromPoll, setBuildInFlightFromPoll] = useState(false);

  // A — reattach poll (cloud-furnace 2026-06-22). The live agent.send
  // chunk stream reaches ONLY the originating connection. After a
  // reconnect / tab-switch / on a second device, this renderer is no
  // longer the originator, so a build running server-side (continuation
  // driver, PR #2118) looks frozen here — and worse, when it FINISHES this
  // renderer never gets the terminal `chunk:done`, so `isAiTyping` stays
  // true and the UI shows "working" forever. `agent:get-status` is the
  // authoritative server-side liveness signal; we drive two things off it:
  //   1. while inFlight AND we're not mid-streaming a local message
  //      (currentAiMsgId===null), merge the journal so progress catches up;
  //   2. when NOT inFlight but the UI still shows busy, do a final merge
  //      then reconcile the build to idle (clears the stuck "working").
  // The merge is the SAME id-deduped projection #2065 uses on load (never
  // clobbers a streamed message, no-ops when nothing's new); on desktop /
  // pre-journal games agent:history yields [] so this is inert.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    let tickInFlight = false;
    // #2893 — whether the PREVIOUS tick observed a server-side build in
    // flight. An in-flight→idle transition must always trigger one final
    // history merge, even when the renderer already shows idle (see the
    // synthetic-done race in `tick` below).
    let lastPollInFlight = false;
    // #3068 — count of CONSECUTIVE unknown (rejected) get-status polls. A
    // rejection is NOT authoritative idle; we hold the last-known liveness
    // across a bounded window (UNKNOWN_STATUS_HOLD_TICKS) before releasing to
    // idle so a gone pod cannot pin "Working…" forever. Reset by any success.
    let consecutiveUnknownStatusPolls = 0;
    // #2893 Codex P1 — the last-assistant-row id the completion sweep has
    // already settled on. `stuckBusy`/`wasInFlight` alone leave a hole: when
    // the observe-reattach path's synthetic `done` clears typing BEFORE the
    // first poll tick ever observes `inFlight: true`, both gates are false
    // and the completed journal is never fetched. Sweeping once per unseen
    // timeline tail closes every ordering of that race (the trigger is the
    // visible timeline itself, not how typing happened to clear) at a
    // bounded cost of one `agent:history` fetch per completed turn.
    let completionSweepSettledFor: string | null = null;
    // #2893 Codex round-3 P2 — failed sweep fetches must not retry every 5s
    // forever (a wedged IPC bridge would otherwise hammer the channel for
    // the session's lifetime). After this many consecutive failures for the
    // SAME sweep key (~1 minute of 5s ticks) the sweep settles with a log;
    // a stuckBusy/wasInFlight trigger or a new turn still re-attempts.
    // Accepted tradeoff (Codex round-4): after the cap settles, a genuinely
    // LATE completion for that same tail stays unfetched until one of those
    // other triggers fires — a narrow silent window, preferred over an
    // unbounded retry loop.
    const MAX_COMPLETION_SWEEP_FETCH_FAILURES = 12;
    let sweepFailureKey: string | null = null;
    let sweepFailureCount = 0;
    /** Last assistant-row id in the timeline, skipping `excludeId` (the
     *  LIVE-streaming placeholder — see the settle site below for why it
     *  must never become the settled sweep key). */
    const lastAssistantIdOf = (
      messages: ReadonlyArray<{ readonly role: string; readonly id: string }>,
      excludeId: string | null,
    ): string | null => {
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m !== undefined && m.role === 'assistant' && m.id !== excludeId) return m.id;
      }
      return null;
    };
    /** Fetch + merge the journal. Outcome for the completion sweep:
     *  'merged' carries the PROJECTED ids so the settle site can verify the
     *  sweep target actually converged; 'empty' (the page resolved but
     *  projected NOTHING); 'failed' (the invoke rejected).
     *  Bugbot (#2899 round-4 Medium): 'empty' must NOT settle the sweep —
     *  the desktop handler degrades to an EMPTY page on pod-fetch failure
     *  and other fail-safe paths WITHOUT rejecting the IPC, so settling on
     *  an empty page would let one degraded response permanently swallow
     *  the completion again. The renderer cannot distinguish "genuinely
     *  empty journal" from "handler degraded", so both count toward the
     *  same retry cap below. */
    type MergeHistoryOutcome =
      | { readonly kind: 'merged'; readonly projectedIds: ReadonlySet<string> }
      | { readonly kind: 'empty' }
      | { readonly kind: 'failed' };
    // crucible#2956 (Codex r6/r7) — the projection terminalises an unterminated
    // turn (withheld/dropped/pod-killed terminal) ONLY when the agent is idle,
    // so a merged reattach never re-introduces a forever-"running" bubble. The
    // liveness that decides this is read STRICTLY AFTER the history snapshot
    // (Codex r7): the poll's own status read predates this fetch, so a turn that
    // starts in between would otherwise be stamped stale-idle. A failed liveness
    // read is "unknown" (undefined → preserve in-flight), never a false that
    // stalls a live turn.
    // #2901 — fetch `agent:history` incrementally and merge. WARM: hand the pod
    // the accumulator's cursor so it returns only the delta past it; UNION the
    // delta onto the accumulator (de-dup by id) and RE-PROJECT the FULL set, so
    // the reducer receives a whole-timeline projection exactly as it did when
    // every fetch pulled the full page (no partial-turn corruption — a turn
    // split across a cold page and a later delta still projects whole). COLD
    // (`forceCold`, or no accumulator yet, or the reconcile interval elapsed):
    // send NO cursor and REPLACE the accumulator with the fresh full page —
    // healing any stale-cursor drift (#3043) within one interval. The projection
    // is liveness-aware (crucible#2956); liveness is read STRICTLY AFTER the
    // history snapshot so a turn that starts mid-fetch is seen in-flight, not
    // stale-idle.
    const fetchAndMergeHistory = async (forceCold: boolean): Promise<MergeHistoryOutcome> => {
      const startedAt = Date.now();
      // Stamp this fetch's START order (#2901 Codex r1) so the commit can order
      // it against a concurrent hydration/fetch and never regress the cursor.
      const mySeq = ++historyFetchSeqRef.current;
      const accAtStart = historyAccumulatorRef.current;
      const haveAcc = accAtStart !== null && accAtStart.gameId === gameId;
      const reconcileDue =
        haveAcc && startedAt - accAtStart.lastColdReconcileAt >= RECONCILE_INTERVAL_MS;
      // Cold by REQUEST (r3 Bugbot): a cursorless request yields a full page, so
      // an accumulator with no cursor to send (old pod / prior cursorless page)
      // is cold — committed authoritative (replace), not union-merged.
      const hasCursor = haveAcc && accAtStart !== null && accAtStart.cursor !== undefined;
      const cold = shouldFetchCold({ forceCold, haveAcc, reconcileDue, hasCursor });
      // `!cold` implies `hasCursor` (shouldFetchCold), hence a non-null accumulator
      // with a cursor; the explicit null check keeps tsc happy across the helper.
      const cursor = !cold && accAtStart !== null ? accAtStart.cursor : undefined;
      const page = await client
        .invoke('agent:history', cursor === undefined ? { gameId } : { gameId, cursor })
        .catch(() => null);
      if (cancelled || page === null || page === undefined) return { kind: 'failed' };
      const agentInFlight = await readAgentLiveness(() =>
        client.invoke('agent:get-status', { gameId }),
      );
      if (cancelled) return { kind: 'failed' };
      // Commit under the generation guard: re-read the CURRENT accumulator (a
      // concurrent hydration/fetch may have committed while we awaited), so a
      // late-resolving older fetch merges its items but never regresses the
      // watermark the fresher fetch installed.
      // #3051 / #3528 — authoritative about row ABSENCE only when this fetch
      // actually performed an authoritative cold REPLACE. `commitHistoryFetch`
      // reports that as an EXPLICIT per-commit boolean (`authoritativeReplace`),
      // set true ONLY on its two replace branches and false on every union merge.
      // A cold read that degrades (non-freshest, empty, or superseded) is
      // UNION-merged and RETAINS the prior accumulator, so it reports
      // `authoritativeReplace: false` even though the request-level `cold` flag is
      // true — dispatching off `cold` there would let the reducer delete rows
      // against a stale retained projection (#3528 Bugbot P1). We therefore
      // dispatch off the commit outcome, NEVER off `cold` and NEVER re-derived
      // from a seq counter (which is reset on a same-game hydration retry — the
      // Codex r3 MAJOR the seq derivation missed).
      // #3528 (LEG 2) — a DEGRADED cold read (`parseErrors > 0`: a
      // previously-projected line went unreadable) must NOT be authoritative
      // about absence. Passing the page's count makes `commitHistoryFetch` fall
      // to UNION/retain and report `authoritativeReplace: false`, so the reducer
      // keeps the held rows rather than deleting a turn whose journal line is
      // now corrupt (crucible#3043 fail-closed).
      const { accumulator: committed, authoritativeReplace } = commitHistoryFetch(
        historyAccumulatorRef.current,
        gameId,
        {
          mySeq,
          cold,
          items: extractJournalItems(page),
          nextCursor: extractHistoryCursor(page),
          now: Date.now(),
          parseErrors: extractPageParseErrors(page),
        },
      );
      historyAccumulatorRef.current = committed;
      const projected = projectAgentHistoryToChatMessages(committed.events, { agentInFlight });
      if (projected.length === 0) return { kind: 'empty' };
      chatDispatch({
        type: 'chat/agent-history-merged',
        messages: projected,
        authoritative: authoritativeReplace,
      });
      return { kind: 'merged', projectedIds: new Set(projected.map((m) => m.id)) };
    };
    const tick = async (): Promise<void> => {
      if (cancelled || tickInFlight) return;
      tickInFlight = true;
      try {
        // #3068 — a REJECTED get-status read is UNKNOWN, not idle. The old
        // `.catch(() => ({ inFlight: false }))` made a failing poll
        // indistinguishable from "build finished": it dropped the Working
        // indicator (the stuckBusy reconcile below) and re-armed the kickoff
        // watchdog (buildInFlightFromPoll → false) off a fabricated idle.
        // readAgentLiveness returns `undefined` on rejection (crucible#2956).
        const liveness = await readAgentLiveness(() =>
          client.invoke('agent:get-status', { gameId }),
        );
        if (cancelled) return;
        let inFlight: boolean;
        if (liveness === undefined) {
          // Unknown: PRESERVE last-known across a bounded window (a transient
          // pod-load blip / WSS reconnect). Skip the tick — do not touch
          // buildInFlightFromPoll, lastPollInFlight, or the reconcile — so a
          // brief outage neither drops "Working…" nor re-arms the watchdog.
          // RELEASE past the cap: a genuinely gone pod rejects every tick, and
          // holding inFlight:true forever would pin "Working…" with the
          // run-state staleness escalation suppressed (gated on !isAiTyping).
          // Falling through to idle here lets the reconcile clear the stuck
          // Working state so a truthful 'stalled' can surface.
          consecutiveUnknownStatusPolls += 1;
          if (consecutiveUnknownStatusPolls < UNKNOWN_STATUS_HOLD_TICKS) return;
          inFlight = false;
        } else {
          consecutiveUnknownStatusPolls = 0;
          inFlight = liveness;
        }
        const wasInFlight = lastPollInFlight;
        lastPollInFlight = inFlight;
        // Surface the authoritative server-side liveness to the kickoff
        // watchdog (see buildInFlightFromPoll). Always set — it must also go
        // false when the build ends.
        setBuildInFlightFromPoll(inFlight);
        if (inFlight) {
          // Catch up progress, but only when we're not the live originator
          // mid-streaming a message (it owns in-place chunk mutation then).
          //
          // #2949 — "the live originator" is a RENDERER-minted placeholder
          // (`startTurn`, `source: 'renderer'`), never an `agent-stream` row.
          // For a SERVER-initiated (plan-gate) build this renderer is only an
          // observer: hydration with `agentInFlight: true` adopts the
          // journal's latest in-flight assistant (`rehydrateInFlightTurn`,
          // `source: 'agent-stream'`) as `currentAiMsgId`, and the old
          // `currentAiMsgId === null` gate then blocked EVERY merge for the
          // build's 30-90 min life — chat frozen at the hydration snapshot,
          // completion announcement swallowed (#2899's surface). An
          // `agent-stream` current row — minted by the journal projection OR
          // persisted by desktop main's chat-stream-persistence and
          // rehydrated (Codex LOW on #2960: 'agent-stream' is main/journal-
          // owned, not projection-exclusive) — has no live chunk stream in
          // THIS renderer to protect (a reload severed any stream that wrote
          // it), so it must not block the observer's only progress channel.
          // An unknown current row (id not in the timeline) stays
          // conservative: treat it as live and skip, byte-identical to the
          // old behaviour.
          const currentId = getChatState().currentAiMsgId;
          const currentRow =
            currentId === null
              ? undefined
              : getChatState().messages.find((m) => m.id === currentId);
          const locallyStreaming =
            currentId !== null &&
            (currentRow === undefined || currentRow.source !== 'agent-stream');
          // crucible#2956 — catch up progress; the fetch re-reads fresh
          // liveness (Codex r7) so terminalisation tracks the true build state.
          // #2901 — while the build runs this is the incremental (warm) path;
          // `fetchAndMergeHistory` self-promotes to a cold reconcile once the
          // interval elapses, so a stale cursor never freezes a live build for
          // more than one interval.
          if (!locallyStreaming) await fetchAndMergeHistory(false);
        } else {
          const stuckBusy = getChatState().isAiTyping;
          // Trigger key excludes the live placeholder: a mid-stream tail
          // must never drive (or later satisfy) the sweep bookkeeping.
          const sweepKey = lastAssistantIdOf(
            getChatState().messages,
            getChatState().currentAiMsgId,
          );
          const needsCompletionSweep = sweepKey !== null && sweepKey !== completionSweepSettledFor;
          if (stuckBusy) {
            // Build is done server-side but this renderer still shows busy —
            // the terminal chunk never reached us. Reconcile FIRST, then
            // merge (#2893): `chat/agent-history-merged` skips the message
            // keyed by `currentAiMsgId` (never clobber a live stream), so
            // the old merge-then-reconcile order silently dropped the
            // completed turn's final assistant message — the user's stream
            // died mid-build and the completion never rendered. Clearing
            // the flight state first lets the merge land the final content.
            chatDispatch({ type: 'chat/agent-flight-reconciled', inFlight: false });
          }
          if ((stuckBusy || wasInFlight || needsCompletionSweep) && !cancelled) {
            // Final backfill so the completed turn's assistant message
            // renders. `wasInFlight` covers the synthetic-done race when a
            // tick had already observed the build running;
            // `needsCompletionSweep` (Codex P1) covers the ordering where
            // the observe-reattach path's synthetic terminal cleared typing
            // before ANY tick saw `inFlight: true` — with only the other
            // two gates the completed journal was never fetched and the
            // completion text existed only server-side, forever (#2893).
            // This is the not-inFlight branch, but the fetch re-reads fresh
            // liveness after the history fetch (Codex r7): a turn that started
            // since the tick's status read is seen as in-flight, not stalled.
            // #2901 — the build is terminal here, so force a COLD reconcile: the
            // completed turn's timeline is served whole + exact (never a stale
            // warm delta that could miss the finalise past a rotation).
            const outcome = await fetchAndMergeHistory(true);
            // Settlement requires the sweep TARGET to have actually
            // converged (Codex round-5 P1): a non-empty page whose rows are
            // all already present (e.g. it carries only an earlier turn's
            // journal) makes the reducer a no-op — settling then would key
            // the sweep on a still-LOCAL tail and permanently swallow that
            // turn's completion, the same hole as the empty-page case one
            // shape over. So settle ONLY when the post-merge tail assistant
            // is JOURNAL-BACKED (its id is in the page we just merged);
            // anything else counts toward the retry cap.
            const postMergeTail =
              outcome.kind === 'merged' && !cancelled
                ? lastAssistantIdOf(
                    // Settle on the POST-merge tail, EXCLUDING the
                    // live-streaming placeholder (Codex round-3 P1): the
                    // reconcile above drains the queue, so a queued turn B
                    // may have JUST started — its fresh placeholder is now
                    // the raw tail, and settling on it would pre-mark B as
                    // swept, leaving B's bubble blank forever if B then
                    // hits the synthetic-done-before-first-poll race.
                    getChatState().messages,
                    getChatState().currentAiMsgId,
                  )
                : null;
            if (
              outcome.kind === 'merged' &&
              !cancelled &&
              postMergeTail !== null &&
              outcome.projectedIds.has(postMergeTail)
            ) {
              completionSweepSettledFor = postMergeTail;
              sweepFailureKey = null;
              sweepFailureCount = 0;
            } else if (!cancelled && needsCompletionSweep) {
              // Codex round-3 P2 + Bugbot round-4 + Codex round-5 —
              // bounded retry: count consecutive non-converging attempts
              // (rejected invokes, empty pages from the handler's degraded
              // fail-safe paths, AND stale non-empty pages that did not
              // converge the sweep target) per sweep key; past the cap,
              // settle with a log so neither a wedged IPC bridge nor a
              // journal that never carries this tail is polled every 5s
              // forever.
              if (sweepFailureKey === sweepKey) {
                sweepFailureCount += 1;
              } else {
                sweepFailureKey = sweepKey;
                sweepFailureCount = 1;
              }
              if (sweepFailureCount >= MAX_COMPLETION_SWEEP_FETCH_FAILURES) {
                console.warn(
                  '[chat] completion sweep giving up after repeated agent:history failures',
                  { gameId, sweepKey, attempts: sweepFailureCount },
                );
                completionSweepSettledFor = sweepKey;
                sweepFailureKey = null;
                sweepFailureCount = 0;
              }
            }
          }
        }
      } catch {
        /* reattach poll is best-effort — never surface a poll failure */
      } finally {
        tickInFlight = false;
      }
    };
    const interval = setInterval(() => void tick(), REATTACH_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [client, gameId, chatDispatch, getChatState]);

  // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — "Try again" on the
  // hydration error state. Flip the reducer back to 'loading' (same-game
  // retry, so the message draft / queue are preserved — NOT a game/changed
  // reset) and bump the attempt key to re-run the mount load.
  const handleRetryHydration = useCallback(() => {
    chatDispatch({ type: 'chat/hydration-retrying' });
    setChatPersistError(null);
    setHydrationAttemptKey((k) => k + 1);
  }, [chatDispatch]);

  return {
    chat,
    chatDispatch,
    setInterpreter,
    getChatState,
    hydratedForGameIdRef,
    pendingCheckpointPushesRef,
    hydrationLongRunning,
    buildInFlightFromPoll,
    setBuildInFlightFromPoll,
    chatPersistError,
    setChatPersistError,
    handleRetryHydration,
  };
}
